import {
  computed,
  inject,
  onMounted,
  onUnmounted,
  provide,
  reactive,
  shallowReactive,
  toValue,
  watch,
  type MaybeRefOrGetter,
} from "vue";
import {
  FormValidationContextKey,
  type AggregatedDirtyField,
  type FieldValidationController,
  type FormValidationContext,
  type FormValidationController,
  type ValidationNode,
} from "../types/validation";

export interface UseFormValidationOptions {
  readonly name?: MaybeRefOrGetter<string>;
  readonly displayName?: MaybeRefOrGetter<string>;
  readonly loading?: MaybeRefOrGetter<boolean>;
  readonly disabled?: MaybeRefOrGetter<boolean>;
  readonly active?: MaybeRefOrGetter<boolean>;
  readonly ignoreValidation?: MaybeRefOrGetter<boolean>;
  readonly contributesDirty?: MaybeRefOrGetter<boolean>;
  readonly propagateValidation?: MaybeRefOrGetter<boolean>;
  readonly path?: MaybeRefOrGetter<readonly string[]>;
}

export function useFormValidation(
  options: UseFormValidationOptions = {},
): FormValidationController {
  const parentContext = inject(FormValidationContextKey, undefined);
  const id = Symbol("ValidationForm");
  const nodes = shallowReactive(new Map<symbol, ValidationNode>());
  const loading = computed(() => toValue(options.loading) ?? false);
  const disabled = computed(() => toValue(options.disabled) ?? false);
  const active = computed(() => toValue(options.active) ?? true);
  const ignoreValidation = computed(() => toValue(options.ignoreValidation) ?? false);
  const contributesDirty = computed(() => toValue(options.contributesDirty) ?? true);
  const propagateValidation = computed(() => toValue(options.propagateValidation) ?? true);
  const path = computed(() => toValue(options.path) ?? []);
  const initialBaselinePending = computed(() => baselineSessionPending.value);
  const initialLoadingCompleted = reactive({ value: !loading.value });
  const baselineSessionPending = reactive({ value: loading.value });
  let previousLoading = loading.value;
  let unregister: (() => void) | undefined;

  watch(loading, (nextLoading) => {
    if (previousLoading && !nextLoading) {
      initialLoadingCompleted.value = true;
      if (baselineSessionPending.value) {
        baselineSessionPending.value = false;
        void commitNodes(true);
      }
    }
    previousLoading = nextLoading;
  });

  const participatingNodes = computed(() =>
    [...nodes.values()].filter((node) => node.participatesInValidation),
  );
  const validationNodes = computed(() => participatingNodes.value.filter((node) => node.hasRules));
  const pending = computed(() => participatingNodes.value.some((node) => node.pending));
  const baselinePending = computed(
    () =>
      initialBaselinePending.value || participatingNodes.value.some((node) => node.baselinePending),
  );
  const invalid = computed(() => validationNodes.value.some((node) => node.invalid));
  const valid = computed(
    () =>
      !pending.value &&
      !baselinePending.value &&
      !invalid.value &&
      validationNodes.value.every((node) => node.valid !== false),
  );
  const dirty = computed(() =>
    [...nodes.values()].some((node) => node.contributesDirty && node.dirty),
  );
  const touched = computed(() =>
    [...nodes.values()].some((node) => node.contributesDirty && node.touched),
  );
  const dirtyFields = computed<readonly AggregatedDirtyField[]>(() =>
    [...nodes.values()].flatMap((node) => {
      if (!node.contributesDirty) {
        return [];
      }

      const entries =
        "value" in node ? fieldDirtyEntries(node as FieldValidationController) : node.dirtyFields;

      return entries.map((entry) => ({
        ...entry,
        path: [...path.value, ...entry.path],
      }));
    }),
  );
  const issues = computed(() =>
    participatingNodes.value.flatMap((node) =>
      node.issues.map((entry) => ({
        ...entry,
        path: [...path.value, ...entry.path],
      })),
    ),
  );

  function register(node: ValidationNode): () => void {
    if (
      import.meta.env.DEV &&
      [...nodes.values()].some((registered) => registered.name === node.name)
    ) {
      globalThis.console.warn(
        `Duplicate validation field name "${node.name}" in the same form context`,
      );
    }
    nodes.set(node.id, node);
    return () => nodes.delete(node.id);
  }

  async function validateNodes(all: boolean): Promise<boolean> {
    const targets = [...nodes.values()].filter((node) => all || node.participatesInValidation);
    const results = await Promise.all(targets.map((node) => node.validate()));
    return results.every(Boolean);
  }

  async function commitNodes(all: boolean): Promise<void> {
    const targets = [...nodes.values()].filter((node) => all || node.participatesInValidation);
    await Promise.all(targets.map((node) => node.commitBaseline()));
  }

  async function resetNodes(all: boolean): Promise<void> {
    const targets = [...nodes.values()].filter((node) => all || node.participatesInValidation);
    await Promise.all(targets.map((node) => node.resetToBaseline()));
  }

  const controller = reactive({
    id,
    get name() {
      return toValue(options.name) ?? "form";
    },
    get displayName() {
      return toValue(options.displayName) ?? "";
    },
    get valid() {
      return valid.value;
    },
    get invalid() {
      return invalid.value;
    },
    get canSubmit() {
      return valid.value && !loading.value && !disabled.value;
    },
    get dirty() {
      return dirty.value;
    },
    get touched() {
      return touched.value;
    },
    get pending() {
      return pending.value;
    },
    get baselinePending() {
      return baselinePending.value;
    },
    get disabled() {
      return disabled.value;
    },
    get hasRules() {
      return validationNodes.value.length > 0;
    },
    get participatesInValidation() {
      return active.value && !ignoreValidation.value && !disabled.value;
    },
    get contributesDirty() {
      return contributesDirty.value && !disabled.value;
    },
    get registeredFieldCount() {
      return nodes.size;
    },
    get dirtyFields() {
      return dirtyFields.value;
    },
    get issues() {
      return issues.value;
    },
    validate: () => validateNodes(false),
    validateAll: () => validateNodes(true),
    commitBaseline: () => commitNodes(false),
    resetToBaseline: () => resetNodes(false),
    commitAllBaselines: () => commitNodes(true),
    resetAllToBaselines: () => resetNodes(true),
    async beginBaselineSession() {
      initialLoadingCompleted.value = !loading.value;
      previousLoading = loading.value;
      await Promise.all([...nodes.values()].map((node) => node.beginBaselineSession()));
      if (!loading.value) {
        baselineSessionPending.value = false;
        await commitNodes(true);
      } else {
        baselineSessionPending.value = true;
      }
    },
    destroy,
  }) as FormValidationController;

  const context = reactive({
    controller,
    get baselinePending() {
      return initialBaselinePending.value;
    },
    get disabled() {
      return disabled.value;
    },
    register,
  }) as FormValidationContext;

  provide(FormValidationContextKey, context);

  function destroy() {
    unregister?.();
    unregister = undefined;
    nodes.forEach((node) => node.destroy());
    nodes.clear();
  }

  onMounted(() => {
    if (parentContext != null && propagateValidation.value) {
      unregister = parentContext.register(controller);
    }
  });
  onUnmounted(destroy);

  return controller;
}

function fieldDirtyEntries(field: FieldValidationController): readonly AggregatedDirtyField[] {
  if (!field.dirty) {
    return [];
  }

  return [
    {
      id: field.id,
      name: field.name,
      displayName: field.displayName,
      path: [],
      value: field.value,
      baseline: field.baseline,
    },
  ];
}
